import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useCustomerAuth from '../hooks/useCustomerAuth';
import useHostAuth from '../hooks/useHostAuth';
import useOtpResendCooldown from '../hooks/useOtpResendCooldown';
import { requestCustomerOtp as sendCustomerOtp, verifyCustomerOtp } from '../api/customer';
import LandingHeader from '../components/landingSnapshot/LandingHeader.jsx';
import PublicSiteFooter from '../../components/Footer/PublicSiteFooter.jsx';

const TabButton = ({ active, children, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`px-4 py-2 rounded-full text-sm font-semibold ${
      active ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
    } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

const normalizePhone = (value) => String(value || '').replace(/\D/g, '');
const digitsOnly = (value, maxLength) => String(value || '').replace(/\D/g, '').slice(0, maxLength);

const normalizeCountryCode = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return `+${digits || '91'}`;
};

const SignupForm = ({ form, setField, loading, otpSent, otpNotice, onSubmit, onResend, resendSeconds }) => (
  <form className="space-y-3" onSubmit={onSubmit}>
    <input
      className="w-full px-3 py-2 rounded-lg border border-slate-300"
      value={form.full_name}
      onChange={(e) => setField('full_name', e.target.value)}
      placeholder="Full Name"
      required
    />
    <div className="flex gap-2">
      <input
        className="w-24 px-3 py-2 rounded-lg border border-slate-300"
        value={form.country_code}
        onChange={(e) => setField('country_code', e.target.value)}
        placeholder="+91"
        inputMode="numeric"
        maxLength={4}
      />
      <input
        className="flex-1 px-3 py-2 rounded-lg border border-slate-300"
        value={form.phone}
        onChange={(e) => setField('phone', e.target.value)}
        placeholder="10-digit mobile number"
        inputMode="numeric"
        maxLength={10}
        pattern="[0-9]{10}"
        required
      />
    </div>
    <input
      type="email"
      className="w-full px-3 py-2 rounded-lg border border-slate-300"
      value={form.email}
      onChange={(e) => setField('email', e.target.value)}
      placeholder="Email"
      required
    />
    <div className="rounded-lg bg-slate-50 px-4 py-2 text-xs text-slate-600">
      Password is not required. Your account will be created after OTP verification.
    </div>
    {otpSent ? (
      <input
        className="w-full px-3 py-2 rounded-lg border border-slate-300"
        value={form.otp}
        onChange={(e) => setField('otp', e.target.value)}
        placeholder="Enter OTP"
        inputMode="numeric"
        maxLength={8}
        required
      />
    ) : null}
    {otpNotice ? (
      <div className="rounded-lg bg-emerald-50 px-4 py-2 text-sm text-emerald-700 whitespace-pre-line">
        {otpNotice}
      </div>
    ) : null}
    <div className="flex gap-2">
      <button
        type="submit"
        disabled={loading}
        className="flex-1 bg-emerald-600 text-white rounded-lg py-2 font-semibold hover:bg-emerald-700"
      >
        {loading ? (otpSent ? 'Verifying OTP...' : 'Sending OTP...') : otpSent ? 'Verify OTP & Sign up' : 'Send OTP'}
      </button>
      {otpSent ? (
        <button
          type="button"
          disabled={loading || resendSeconds > 0}
          onClick={onResend}
          className="rounded-lg border border-slate-300 px-4 py-2 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {resendSeconds > 0 ? `Resend in ${resendSeconds}s` : 'Resend'}
        </button>
      ) : null}
    </div>
    <p className="text-xs text-slate-500">
      {otpSent
        ? 'OTP sent to your mobile number. If you change signup details, request OTP again.'
        : 'We will send an OTP to your mobile number before creating the account.'}
    </p>
  </form>
);

const CustomerAuthPageView = ({ customerAuth, hostAuth, defaultTab = 'customer-signup' }) => {
  const [tab, setTab] = useState(defaultTab);
  const [signupForm, setSignupForm] = useState({
    full_name: '',
    country_code: '+91',
    phone: '',
    email: '',
    otp: '',
  });
  const [hostSignupForm, setHostSignupForm] = useState({
    full_name: '',
    business_name: '',
    country_code: '+91',
    phone: '',
    email: '',
    password: '',
    confirm: '',
  });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [customerOtpSent, setCustomerOtpSent] = useState(false);
  const { resendSeconds, resendBlocked, startResendCooldown, resetResendCooldown } = useOtpResendCooldown();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get('next');
  const customerRedirectTo = nextParam && nextParam.startsWith('/') ? nextParam : '/';

  useEffect(() => {
    if (!customerAuth.loading && customerAuth.customer) {
      navigate(customerRedirectTo, { replace: true });
    }
  }, [customerAuth.loading, customerAuth.customer, customerRedirectTo, navigate]);

  useEffect(() => {
    if (!hostAuth.loading && hostAuth.host) {
      navigate('/host/dashboard', { replace: true });
    }
  }, [hostAuth.loading, hostAuth.host, navigate]);

  const setCustomerSignupField = (field, value) => {
    const resetsOtp = customerOtpSent && field !== 'otp';
    const nextValue =
      field === 'phone'
        ? digitsOnly(value, 10)
        : field === 'country_code'
          ? `+${digitsOnly(value, 3) || '91'}`
          : field === 'otp'
            ? digitsOnly(value, 8)
            : value;
    setSignupForm((current) => ({
      ...current,
      [field]: nextValue,
      ...(resetsOtp ? { otp: '' } : {}),
    }));
    if (error) setError('');
    if (notice) setNotice('');
    if (resetsOtp) {
      setCustomerOtpSent(false);
      resetResendCooldown();
    }
  };

  const requestCustomerOtp = async () => {
    const normalizedPhone = normalizePhone(signupForm.phone);
    if (!/^\d{10}$/.test(normalizedPhone)) {
      setError('Enter a valid 10-digit mobile number');
      return;
    }
    if (!signupForm.email.includes('@')) {
      setError('Enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const res = await sendCustomerOtp({
        identifier_type: 'phone',
        full_name: signupForm.full_name.trim(),
        country_code: normalizeCountryCode(signupForm.country_code),
        phone: normalizedPhone,
        email: signupForm.email.trim(),
      });
      setCustomerOtpSent(true);
      startResendCooldown();
      const requestId = String(res?.requestId || '').trim();
      setNotice(
        requestId
          ? `${res?.message || 'OTP sent to your mobile number.'}\nRequest ID: ${requestId}`
          : res?.message || 'OTP sent to your mobile number.'
      );
    } catch (err) {
      setError(err.message || 'Unable to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!customerOtpSent) {
      await requestCustomerOtp();
      return;
    }
    if (!String(signupForm.otp || '').trim()) {
      setError('Enter the OTP sent to your mobile number');
      return;
    }
    setNotice('');
    setLoading(true);
    try {
      const res = await verifyCustomerOtp({
        identifier_type: 'phone',
        full_name: signupForm.full_name.trim(),
        country_code: normalizeCountryCode(signupForm.country_code),
        phone: normalizePhone(signupForm.phone),
        email: signupForm.email.trim(),
        otp: String(signupForm.otp || '').trim(),
      });
      await customerAuth.applyAuth(res);
      navigate(customerRedirectTo, { replace: true });
    } catch (err) {
      setError(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignupOtpResend = async () => {
    if (resendBlocked) return;
    setError('');
    setNotice('');
    await requestCustomerOtp();
  };

  const handleHostSignup = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    if (hostSignupForm.password !== hostSignupForm.confirm) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await hostAuth.signup({
        full_name: hostSignupForm.full_name,
        business_name: hostSignupForm.business_name,
        country_code: hostSignupForm.country_code,
        phone: hostSignupForm.phone,
        email: hostSignupForm.email || undefined,
        password: hostSignupForm.password,
      });
      navigate('/host/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LandingHeader />
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-4xl bg-white shadow-lg rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap gap-2 mb-6">
          <TabButton active={tab === 'customer-signup'} onClick={() => setTab('customer-signup')}>
            Customer Signup
          </TabButton>
          <TabButton active={tab === 'host-signup'} onClick={() => setTab('host-signup')}>
            Host Signup
          </TabButton>
        </div>

        {error && <div className="mb-4 rounded-lg bg-red-50 text-red-700 px-4 py-2">{error}</div>}

        {tab === 'customer-signup' && (
          <div className="space-y-4">
            <SignupForm
              form={signupForm}
              setField={setCustomerSignupField}
              loading={loading}
              otpSent={customerOtpSent}
              otpNotice={notice}
              onSubmit={handleSignup}
              onResend={handleSignupOtpResend}
              resendSeconds={resendSeconds}
            />
            <p className="text-sm text-slate-500">
              Already have an account?{' '}
              <Link to="/customer/login" className="text-emerald-700 font-semibold hover:underline">
                Guest Login
              </Link>
            </p>
          </div>
        )}

        {tab === 'host-signup' && (
          <form className="space-y-3" onSubmit={handleHostSignup}>
            <input
              className="w-full px-3 py-2 rounded-lg border border-slate-300"
              value={hostSignupForm.full_name}
              onChange={(e) => setHostSignupForm((f) => ({ ...f, full_name: e.target.value }))}
              placeholder="Full Name"
              required
            />
            <input
              className="w-full px-3 py-2 rounded-lg border border-slate-300"
              value={hostSignupForm.business_name}
              onChange={(e) => setHostSignupForm((f) => ({ ...f, business_name: e.target.value }))}
              placeholder="Business Name (optional)"
            />
            <div className="flex gap-2">
              <input
                className="w-24 px-3 py-2 rounded-lg border border-slate-300"
                value={hostSignupForm.country_code}
                onChange={(e) => setHostSignupForm((f) => ({ ...f, country_code: e.target.value }))}
                placeholder="+91"
              />
              <input
                className="flex-1 px-3 py-2 rounded-lg border border-slate-300"
                value={hostSignupForm.phone}
                onChange={(e) => setHostSignupForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="Phone number"
                required
              />
            </div>
            <input
              type="email"
              className="w-full px-3 py-2 rounded-lg border border-slate-300"
              value={hostSignupForm.email}
              onChange={(e) => setHostSignupForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="Email (optional)"
            />
            <div className="flex gap-2">
              <input
                type="password"
                className="flex-1 px-3 py-2 rounded-lg border border-slate-300"
                value={hostSignupForm.password}
                onChange={(e) => setHostSignupForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Password"
                required
              />
              <input
                type="password"
                className="flex-1 px-3 py-2 rounded-lg border border-slate-300"
                value={hostSignupForm.confirm}
                onChange={(e) => setHostSignupForm((f) => ({ ...f, confirm: e.target.value }))}
                placeholder="Confirm"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-600 text-white rounded-lg py-2 font-semibold hover:bg-emerald-700"
            >
              {loading ? 'Creating host...' : 'Sign up as Host'}
            </button>
            <p className="text-sm text-slate-500">
              Already a host?{' '}
              <Link to="/host/login" className="text-emerald-700 font-semibold hover:underline">
                Host Login
              </Link>
            </p>
          </form>
        )}
      </div>
      </div>
      <PublicSiteFooter />
    </>
  );
};

const CustomerAuthPageWithAuth = ({ defaultTab }) => {
  const customerAuth = useCustomerAuth();
  const hostAuth = useHostAuth();
  return <CustomerAuthPageView customerAuth={customerAuth} hostAuth={hostAuth} defaultTab={defaultTab} />;
};

const CustomerAuthPage = ({ customerAuth, hostAuth, defaultTab = 'customer-signup' }) => {
  if (customerAuth && hostAuth) {
    return <CustomerAuthPageView customerAuth={customerAuth} hostAuth={hostAuth} defaultTab={defaultTab} />;
  }
  return <CustomerAuthPageWithAuth defaultTab={defaultTab} />;
};

export default CustomerAuthPage;
